import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Clock, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ExitIntentPopup({ 
  isOpen, 
  onClose, 
  couponCode, 
  discountAmount, 
  isFlatDiscount, 
  expiryDate, 
  onProceedToPlans,
  onContinueLogout 
}) {
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Parse expiryDate array from backend [year, month, day, hour, minute, second, nano]
  // and calculate initial time left
  useEffect(() => {
    console.log("🕐 ExitIntentPopup - Received expiryDate:", expiryDate);
    
    if (!expiryDate || !Array.isArray(expiryDate) || expiryDate.length < 5) {
      // Fallback to 24 hours if no valid expiry date (need at least 5 elements: year, month, day, hour, minute)
      console.log("⚠️ Invalid expiry date, falling back to 24 hours");
      setTimeLeft(24 * 60 * 60);
      return;
    }

    try {
      // Parse the array: [year, month, day, hour, minute, second?, nano?]
      // Note: JavaScript months are 0-indexed, backend sends 1-indexed
      const expiryDateTime = new Date(
        expiryDate[0], // year
        expiryDate[1] - 1, // month (convert to 0-indexed)
        expiryDate[2], // day
        expiryDate[3] || 0, // hour
        expiryDate[4] || 0, // minute
        expiryDate[5] || 0  // second (optional)
      );

      console.log("📅 Parsed expiry date:", expiryDateTime.toString());
      console.log("📅 Expiry timestamp:", expiryDateTime.getTime());

      // Calculate time left in seconds
      const now = new Date();
      console.log("📅 Current time:", now.toString());
      console.log("📅 Current timestamp:", now.getTime());
      
      const timeDiffMs = expiryDateTime.getTime() - now.getTime();
      const timeDiffSeconds = Math.floor(timeDiffMs / 1000);

      console.log("⏱️ Time difference (ms):", timeDiffMs);
      console.log("⏱️ Time difference (seconds):", timeDiffSeconds);
      console.log("⏱️ Time difference (hours):", (timeDiffSeconds / 3600).toFixed(2));

      if (timeDiffSeconds <= 0) {
        console.log("❌ Coupon already expired");
        setIsExpired(true);
        setTimeLeft(0);
      } else {
        console.log("✅ Setting countdown to:", timeDiffSeconds, "seconds");
        setTimeLeft(timeDiffSeconds);
        setIsExpired(false);
      }
    } catch (error) {
      console.error("Error parsing expiry date:", error);
      // Fallback to 24 hours
      setTimeLeft(24 * 60 * 60);
    }
  }, [expiryDate]);

  useEffect(() => {
    if (!isOpen || isExpired) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, isExpired]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleProceed = () => {
    // Call the parent's proceed handler (cancels logout and closes popup)
    onProceedToPlans();
    // Navigate to membership plans
    navigate("/membershipPlans");
  };

  const handleCopyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopied(true);
      // Reset after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = couponCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[340px] sm:max-w-sm relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-2 right-2 p-1 rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors cursor-pointer z-50"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-slate-400" />
            </button>

            {/* Content */}
            <div className="relative z-0 p-4 sm:p-5">
              {/* Animated Gift Icon */}
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, -10, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
                className="mx-auto w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg mb-2 sm:mb-3"
              >
                <Gift className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
              </motion.div>

              {/* Headline */}
              <h2 className="text-lg sm:text-xl font-bold text-center mb-1 bg-gradient-to-r from-gray-900 via-orange-600 to-pink-600 dark:from-white dark:via-orange-400 dark:to-pink-400 bg-clip-text text-transparent leading-tight">
                WAIT! Before You Leave...
              </h2>

              {/* Subheadline */}
              <p className="text-center text-gray-600 dark:text-slate-400 text-[10px] sm:text-xs mb-3 sm:mb-4">
                Don't miss this exclusive offer! 🎉
              </p>

              {/* Offer Box */}
              <div className="bg-gradient-to-br from-orange-50 to-pink-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl p-2.5 sm:p-3 border-2 border-orange-200 dark:border-orange-700/50 mb-2.5 sm:mb-3 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-1 sm:gap-1.5 mb-1 sm:mb-1.5">
                    <Tag className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-orange-600 dark:text-orange-400" />
                    <p className="text-[9px] sm:text-[10px] font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wide">
                      Exclusive Offer
                    </p>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold text-center mb-0.5 text-gray-900 dark:text-white">
                    {isFlatDiscount ? `₹${discountAmount} OFF` : `${discountAmount}% OFF`}
                  </h3>
                  <p className="text-center text-[10px] sm:text-[11px] text-gray-600 dark:text-slate-400 mb-2 sm:mb-2.5">
                    on your First Subscription
                  </p>

                  {/* Coupon Code */}
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-2 sm:p-2.5 border-2 border-dashed border-orange-300 dark:border-orange-700">
                    <p className="text-[9px] sm:text-[10px] text-gray-600 dark:text-slate-400 text-center mb-1">
                      Use Coupon Code
                    </p>
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <code className="text-base sm:text-lg font-bold tracking-wider text-orange-600 dark:text-orange-400">
                        {couponCode}
                      </code>
                      <button
                        onClick={handleCopyCoupon}
                        className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-medium transition-all duration-200 ${
                          copied
                            ? 'bg-green-500 text-white'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        {copied ? '✓ Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timer */}
              <div className={`flex items-center justify-center gap-1 sm:gap-1.5 mb-2.5 sm:mb-3 rounded-lg p-1.5 sm:p-2 border ${
                isExpired 
                  ? 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/20 dark:to-gray-900/20 border-gray-300 dark:border-gray-700/50'
                  : 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800/50'
              }`}>
                <Clock className={`w-2.5 sm:w-3 h-2.5 sm:h-3 flex-shrink-0 ${
                  isExpired ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'
                }`} />
                <p className={`text-[10px] sm:text-[11px] font-semibold ${
                  isExpired ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {isExpired ? 'Coupon Expired' : `Expires in: ${formatTime(timeLeft)}`}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-1.5 sm:space-y-2">
                <button
                  onClick={handleProceed}
                  disabled={isExpired}
                  className={`w-full py-2 sm:py-2.5 rounded-lg font-bold text-xs sm:text-sm shadow-lg transition-all duration-300 ${
                    isExpired
                      ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white hover:shadow-xl'
                  }`}
                >
                  {isExpired ? 'Offer Expired' : 'Claim My Offer Now! 🎁'}
                </button>
                <button
                  onClick={onContinueLogout || onClose}
                  className="w-full py-1 sm:py-1.5 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 text-[10px] sm:text-[11px] font-medium transition-colors"
                >
                  {isExpired ? 'Close' : 'No thanks, Continue Logout'}
                </button>
              </div>

              {/* Fine Print */}
              <p className="text-[8px] sm:text-[9px] text-center text-gray-500 dark:text-slate-500 mt-1.5 sm:mt-2">
                * Valid for first-time subscribers only
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
